import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { KeyRound, Ban, Power } from 'lucide-react'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { ConfirmDialog } from '../ui/ConfirmDialog'
import { extractApiError } from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import {
  softwareWatchApi,
  keyStatusLabel,
} from '../../lib/software-watch-api'

const STATUS_STYLE = {
  Active: 'bg-[color:var(--color-success)] text-white dark:bg-white/[0.06] dark:text-emerald-300',
  Inactive: 'bg-[color:var(--color-ink-subtle)] text-white dark:bg-white/[0.06] dark:text-[color:var(--color-ink-muted)]',
  Revoked: 'bg-[color:var(--color-priority-urgent)] text-white dark:bg-white/[0.06] dark:text-rose-300',
  Expired: 'bg-amber-500 text-white dark:bg-white/[0.06] dark:text-amber-300',
}

function KeyStatusBadge({ label }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLE[label] ?? STATUS_STYLE.Inactive}`}
    >
      {label}
    </span>
  )
}

function Info({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-[color:var(--color-ink-muted)]">{label}</div>
      <div className="mt-1 text-[color:var(--color-ink-strong)] break-words">{children}</div>
    </div>
  )
}

/**
 * Consumer details + API-key management. Reads the freshest consumer from the
 * shared consumers query (so key actions reflect immediately after refetch).
 * Per-key actions: Enable, Disable, Revoke.
 */
export function ConsumerDetailsDrawer({ consumerId, onClose }) {
  const qc = useQueryClient()
  const [revokeTarget, setRevokeTarget] = useState(null)

  const { data } = useQuery({
    queryKey: ['sw-consumers'],
    queryFn: softwareWatchApi.listConsumers,
  })
  const consumer = data?.data.find((c) => c._id === consumerId) ?? null

  const toggleKey = useMutation({
    mutationFn: ({ keyId, enabled }) =>
      softwareWatchApi.updateKey(consumerId, keyId, { enabled }),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ['sw-consumers'] })
      toast.success(vars.enabled ? 'Key enabled successfully.' : 'Key disabled successfully.')
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const revokeKey = useMutation({
    mutationFn: (keyId) => softwareWatchApi.revokeKey(consumerId, keyId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sw-consumers'] })
      setRevokeTarget(null)
      toast.success('Key revoked successfully.')
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const keys = consumer?.keys ?? []

  return (
    <>
      <Modal
        open={Boolean(consumerId)}
        onClose={onClose}
        title={consumer ? consumer.name : 'Data Platform'}
        description="Data platform details and API keys."
        size="xl"
        footer={<Button variant="secondary" onClick={onClose}>Close</Button>}
      >
        {!consumer ? (
          <p className="text-sm text-[color:var(--color-ink-muted)]">Data platform not found.</p>
        ) : (
          <div className="space-y-5">
            {/* Consumer info */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              <Info label="Status">
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${consumer.enabled ? STATUS_STYLE.Active : STATUS_STYLE.Inactive}`}>
                  {consumer.enabled ? 'Active' : 'Inactive'}
                </span>
              </Info>
              <Info label="Keys">{keys.length}</Info>
              <Info label="Software">SoftwareWatch</Info>
              <Info label="Created">{formatDateTime(consumer.createdAt)}</Info>
              <Info label="Updated">{formatDateTime(consumer.updatedAt)}</Info>
            </div>

            {/* Keys — a card per key (avoids a wide, horizontally-scrolling
                table so every field stays visible in the modal). */}
            <div>
              <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] flex items-center gap-2 mb-3">
                <KeyRound size={16} className="text-[color:var(--color-accent-strong)]" /> API Keys
              </h3>

              {keys.length === 0 ? (
                <p className="text-sm text-[color:var(--color-ink-muted)] rounded-lg border border-[color:var(--color-rule)] px-4 py-6 text-center">
                  No keys on this platform.
                </p>
              ) : (
                <div className="space-y-3">
                  {keys.map((k) => {
                    const status = keyStatusLabel(k)
                    const revoked = Boolean(k.revokedAt)
                    return (
                      <div key={k._id} className="rounded-lg border border-[color:var(--color-rule)] p-4">
                        {/* Header — label + status, with lifecycle actions. */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-[color:var(--color-ink-strong)]">{k.label || 'Untitled key'}</span>
                              <KeyStatusBadge label={status} />
                            </div>
                            <div className="mt-1 font-mono text-xs text-[color:var(--color-ink-muted)] break-all">
                              {k.keyPrefix || '—'}…{k.last4 || '—'}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {!revoked ? (
                              <>
                                <button
                                  type="button"
                                  title={k.enabled ? 'Disable' : 'Enable'}
                                  aria-label={k.enabled ? 'Disable key' : 'Enable key'}
                                  disabled={toggleKey.isPending}
                                  onClick={() => toggleKey.mutate({ keyId: k._id, enabled: !k.enabled })}
                                  className="p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-canvas)] cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <Power size={16} strokeWidth={1.6} />
                                </button>
                                <button
                                  type="button"
                                  title="Revoke"
                                  aria-label="Revoke key"
                                  onClick={() => setRevokeTarget(k)}
                                  className="p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-canvas)] cursor-pointer transition-colors"
                                >
                                  <Ban size={16} strokeWidth={1.6} />
                                </button>
                              </>
                            ) : (
                              <span className="text-xs text-[color:var(--color-ink-muted)]">revoked</span>
                            )}
                          </div>
                        </div>

                        {/* Fields — wrap into a responsive grid, no scroll. */}
                        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-4 text-sm">
                          <Info label="Scopes">
                            {k.scopes?.length ? (
                              <div className="flex flex-wrap gap-1">
                                {k.scopes.map((s) => (
                                  <span key={s} className="inline-flex rounded bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)] px-1.5 py-0.5 text-xs font-mono">
                                    {s}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-[color:var(--color-ink-muted)]">none</span>
                            )}
                          </Info>
                          <Info label="Allowed IPs">{k.allowedIps?.length ? k.allowedIps.join(', ') : 'Any'}</Info>
                          <Info label="Expires">{k.expiresAt ? formatDateTime(k.expiresAt) : 'Never'}</Info>
                          <Info label="Last used">{k.lastUsedAt ? formatDateTime(k.lastUsedAt) : '—'}</Info>
                          <Info label="Uses">{k.usageCount ?? 0}</Info>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={revokeTarget !== null}
        title="Revoke API Key"
        message={
          revokeTarget
            ? `Revoke the key ${revokeTarget.keyPrefix}…${revokeTarget.last4}? Any sync using it will immediately start receiving 401 responses. This cannot be undone.`
            : ''
        }
        confirmLabel="Revoke Key"
        variant="danger"
        confirming={revokeKey.isPending}
        onCancel={() => setRevokeTarget(null)}
        onConfirm={() => revokeTarget && revokeKey.mutate(revokeTarget._id)}
      />
    </>
  )
}
